import { useRef, useEffect, useState } from 'react';
import { HeroCredentials, HeroShippingNote } from './TrustPlates';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POWER_WORDS = ['CONTROL', 'POWER', 'SPIN', 'CONSISTENCY'];
const HERO_LOGO = 'https://customer-assets.emergentagent.com/job_b7b64ad3-38ad-44dd-8a38-e1acc295afe8/artifacts/2036j8e1_DYSSC_logo_transparent_4x.png';
const TICKER = 'T700 CARBON · 16MM PP CORE · PBCoR .43 · 7.6–8.0 OZ';

// Physics-simulated path: fall -> bounce on the "C" (cap-height floor) -> step down onto the "o"
// (x-height floor) -> smooth roll across o-r-e -> drop off the final "e" -> settle and stay.
// Dense 60fps keyframes + linear interpolation = no easing seams; final frame == resting spot exactly.
// geo: where the ball lands on the wordmark (fractions of its width/height) — defaults tuned for "HexCore"
export const simulateBall = (startLeft, wr, adjTop, B, restLeft, restT, startTop = -110, geo = {}) => {
  const { land = 0.47, edge = 0.55, xh = 0.28 } = geo;
  const w = wr.width;
  const Fc = adjTop - B - 2;                       // floor on cap letters (top of C)
  const Fo = adjTop + wr.height * xh - B - 2;      // floor on lowercase letters (x-height)
  const cLeft = wr.left + w * land - B / 2;        // land on the capital
  const cEdge = wr.left + w * edge - B / 2;        // right edge of the capital -> step down
  const dropEdge = wr.right - B / 2;               // right edge of the final "e" -> final drop
  const tFall = 0.62;
  const g = (2 * Math.max(Fc - startTop, 40)) / (tFall * tFall);
  const dt = 1 / 60;
  const pts = [];
  let x = startLeft, y = startTop, vy = 0, vx = 0, rot = 0, sq = 1, t = 0, rollV = 0;
  let phase = 'fall';
  const roll = (v) => { rot += ((v * dt) / (B / 2)) * 57.3; };
  while (t < 5 && phase !== 'done') {
    if (phase === 'fall') {
      vy += g * dt; y += vy * dt;
      x = startLeft + (cLeft - startLeft) * Math.min(t / tFall, 1);
      rot += 110 * dt;
      if (y >= Fc) { y = Fc; vy = -vy * 0.5; sq = 0.84; phase = 'bounceC'; vx = 70; }
    } else if (phase === 'bounceC') {
      vy += g * dt; y += vy * dt; x += vx * dt; rot += 70 * dt;
      if (y >= Fc) {
        y = Fc; vy = -vy * 0.5; sq = 0.88;
        if (Math.abs(vy) < 110) { vy = 0; phase = 'rollC'; rollV = 120; }
      }
    } else if (phase === 'rollC') {
      rollV += (170 - rollV) * 1.2 * dt;
      x += rollV * dt; y = Fc; roll(rollV);
      if (x >= cEdge) { phase = 'step'; vx = rollV; vy = 0; }
    } else if (phase === 'step') {
      vy += g * dt; y += vy * dt; x += vx * dt; roll(vx);
      if (y >= Fo) {
        y = Fo; vy = -vy * 0.35; sq = 0.9;
        if (Math.abs(vy) < 60) { vy = 0; phase = 'roll'; rollV = vx; }
      }
    } else if (phase === 'roll') {
      rollV += (150 - rollV) * 1.0 * dt;
      x += rollV * dt; y = Fo; roll(rollV);
      if (x >= dropEdge) { phase = 'drop'; vx = rollV; vy = 0; }
    } else if (phase === 'drop') {
      vy += g * dt; y += vy * dt;
      const nx = Math.min(x + vx * dt, restLeft);
      roll((nx - x) / dt); x = nx;
      if (y >= restT) {
        y = restT; vy = -vy * 0.3; vx *= 0.35; sq = 0.9;
        if (Math.abs(vy) < 45) { vy = 0; phase = 'settle'; }
      }
    } else if (phase === 'settle') {
      const dx = (restLeft - x) * 8 * dt;
      x += dx; y = restT; roll(dx / dt);
      if (Math.abs(x - restLeft) < 0.6) { phase = 'done'; }
    }
    sq += (1 - sq) * 10 * dt;
    t += dt;
    pts.push([t, x, y, rot, sq]);
  }
  pts.push([t + dt, restLeft, restT, rot, 1]); // exact final frame == static resting ball position
  const dur = pts[pts.length - 1][0];
  return {
    left: pts.map((p) => p[1]),
    top: pts.map((p) => p[2]),
    rotate: pts.map((p) => p[3]),
    scaleY: pts.map((p) => p[4]),
    times: pts.map((p) => p[0] / dur),
    dur,
  };
};

const Pickleball = ({ className }) => (
  <img
    src={`${API}/static/brand/pickleball.png?v=3`}
    alt=""
    aria-hidden="true"
    draggable={false}
    className={`${className} select-none`}
  />
);

export const Hero3D = () => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [wordIdx, setWordIdx] = useState(0);
  const tickerRef = useRef(null);
  const cursorRef = useRef(null);
  const [ball, setBall] = useState(null);const handleExplore = (e) => {
    const el = document.getElementById('shop');
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const bx = rect.left + rect.width / 2;
    const startY = window.scrollY;
    const targetY = el.getBoundingClientRect().top + startY - 52;

    // physics-simulated: fall -> bounces on the "C" -> smooth roll -> drop next to the final "e" and rest
    const wm = null; // ball no longer lands on the HexCore wordmark
    const vh = window.innerHeight;
    const B = 68; // ball size px
    if (wm) {
      const wr = wm.getBoundingClientRect();
      // exact pending reveal offset (translateY on the h2) instead of a hardcoded guess
      let ty = 0;
      const h2 = wm.closest('h2');
      if (h2) {
        const tr = window.getComputedStyle(h2).transform;
        if (tr && tr !== 'none') { try { ty = new DOMMatrix(tr).f || 0; } catch (err) { ty = 0; } }
      }
      const adjTop = (wr.top + startY) - targetY - ty; // wm top post-landing, post-reveal
      // measure the resting element's CENTER (rotation-invariant) so the animation ends on its exact pixels
      const restEl = document.querySelector("[data-testid='resting-pickleball']");
      let restLeft = wr.right + 6;
      let restT = adjTop + wr.height - B + 2;
      if (restEl) {
        const rr = restEl.getBoundingClientRect();
        const rcx = rr.left + rr.width / 2;
        const rcy = rr.top + rr.height / 2;
        restLeft = rcx - B / 2;
        restT = (rcy - B / 2 + startY) - targetY - ty;
      }
      const path = simulateBall(bx - B / 2, wr, adjTop, B, restLeft, restT);
      setBall(path);
      window.dispatchEvent(new CustomEvent('dyssc-ball-launch'));
      const finalRot = path.rotate[path.rotate.length - 1] % 360;
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('dyssc-ball-rest', { detail: { rot: finalRot } }));
        setTimeout(() => setBall(null), 40); // same-frame swap: identical pixels + rotation
      }, path.dur * 1000);
    } else {
      setBall({
        top: [-110, vh + 130], left: [bx - B / 2, bx - B / 2], scaleY: [1, 1], rotate: [0, 380],
        times: [0, 1], dur: 1.25,
      });
      setTimeout(() => setBall(null), 1500);
    }
    const dur = 650;
    const t0 = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchmove', cancel, { passive: true, once: true });
    const step = (now) => {
      if (cancelled) return;
      const p = Math.min((now - t0) / dur, 1);
      window.scrollTo({ top: startY + (targetY - startY) * ease(p), behavior: 'instant' });
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  useEffect(() => {
    const t = setInterval(() => setWordIdx((i) => (i + 1) % POWER_WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);

  // Typewriter ticker writes straight to the DOM — no per-character React re-render of the hero
  useEffect(() => {
    let iv;
    let n = 0;
    const t = setTimeout(() => {
      iv = setInterval(() => {
        n += 1;
        if (tickerRef.current) tickerRef.current.textContent = TICKER.slice(0, n);
        if (n >= TICKER.length) {
          clearInterval(iv);
          if (cursorRef.current) cursorRef.current.classList.add('animate-pulse');
        }
      }, 26);
    }, 1500);
    return () => { clearTimeout(t); if (iv) clearInterval(iv); };
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  // Parallax movements for floating orbs
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -350]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [videoOn, setVideoOn] = useState(false);
  // Mobile: paint the poster first (LCP), then fade the video in once the page is idle
  // and the connection is decent. Desktop: autoplay immediately as before.
  const [deferVideo] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Start very slow, then step up gradually. Discrete steps prevent frame-by-frame
    // playbackRate updates that cause judder/jitter on most browsers.
    const steps = [
      { time: 0, rate: 0.25 },
      { time: 2500, rate: 0.4 },
      { time: 5000, rate: 0.55 },
      { time: 7500, rate: 0.65 },
    ];

    video.playbackRate = steps[0].rate;
    const onPlaying = () => setVideoOn(true);
    video.addEventListener('playing', onPlaying, { once: true });

    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    let timers = [];
    let idleId = null;
    let cancelled = false;
    const startVideo = () => {
      if (cancelled) return;
      if (deferVideo) {
        const c = navigator.connection;
        if (c && (c.saveData || /(^|-)2g$|^3g$/.test(c.effectiveType || ''))) return; // stay on poster
        [['/hero-bg.mp4', 'video/mp4'], ['/hero-bg.webm', 'video/webm']].forEach(([src, type]) => {
          const s = document.createElement('source');
          s.src = src; s.type = type; video.appendChild(s);
        });
        video.load();
      }
      tryPlay();
      video.addEventListener('canplay', tryPlay, { once: true });
      timers = steps.slice(1).map((s) =>
        setTimeout(() => {
          if (videoRef.current) videoRef.current.playbackRate = s.rate;
        }, s.time)
      );
    };

    if (deferVideo) {
      const kick = () => {
        idleId = 'requestIdleCallback' in window
          ? window.requestIdleCallback(startVideo, { timeout: 2500 })
          : setTimeout(startVideo, 1200);
      };
      if (document.readyState === 'complete') kick();
      else window.addEventListener('load', kick, { once: true });
    } else {
      startVideo();
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      else if (idleId) clearTimeout(idleId);
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('playing', onPlaying);
    };
  }, [deferVideo]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#0B0B0C] text-white pt-6 md:pt-8 pb-10 md:pb-14 min-h-[90vh] flex items-center"
      data-testid="hero-section"
      aria-label="DYSSC Premium Pickleball Paddles - USA Approved"
    >
      {/* Full screen background - poster paints first (LCP), video cross-fades in */}
      <div className="absolute inset-0" style={{ zIndex: 0 }}>
        <img
          src="/hero-bg-poster.jpg"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoOn ? 'opacity-0' : 'opacity-40'}`}
          data-testid="hero-poster"
        />
        <video
          ref={videoRef}
          poster="/hero-bg-poster.jpg"
          autoPlay={!deferVideo}
          loop
          muted
          playsInline
          preload={deferVideo ? 'none' : 'metadata'}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoOn ? 'opacity-40' : 'opacity-0'}`}
          title="DYSSC Pickleball Paddles in Action"
          data-testid="hero-video"
          data-deferred={deferVideo ? '1' : '0'}
        >
          {!deferVideo && <source src="/hero-bg.mp4" type="video/mp4" />}
          {!deferVideo && <source src="/hero-bg.webm" type="video/webm" />}
        </video>
      </div>

      {/* Parallax Floating Elements */}
      <motion.div 
        className="absolute top-20 right-[15%] w-64 h-64 rounded-full pointer-events-none"
        style={{ 
          y: y1,
          background: 'radial-gradient(circle, rgba(224,33,40,0.16) 0%, rgba(224,33,40,0.06) 40%, transparent 70%)',
          zIndex: 1
        }}
      />
      <motion.div 
        className="absolute bottom-[30%] left-[10%] w-72 h-72 rounded-full pointer-events-none"
        style={{ 
          y: y2,
          background: 'radial-gradient(circle, rgba(224,33,40,0.12) 0%, rgba(224,33,40,0.05) 40%, transparent 70%)',
          zIndex: 1
        }}
      />
      
      {/* Red flares */}
      <div className="red-flare-1" style={{ zIndex: 2 }} />
      <div className="red-flare-2" style={{ zIndex: 2 }} />
      
      {/* Shipping note — directly under the header */}
      <div className="absolute top-0 inset-x-0 pt-4 sm:pt-5 md:pt-6" style={{ zIndex: 11 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeroShippingNote />
        </div>
      </div>

      {/* Content layer */}
      <motion.div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative" 
        style={{ zIndex: 10, opacity }}
      >
        <div className="max-w-2xl">
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            
            <motion.div 
              className="flex flex-col items-start"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="overflow-hidden h-28 sm:h-[157px] lg:h-[202px] xl:h-[246px]" data-testid="hero-logo">
                <div className="relative h-full w-max -ml-[50px] sm:-ml-[71px] lg:-ml-[92px] xl:-ml-[112px]">
                  {/* Glow hugs the letterforms only: a blurred layer masked by the wordmark, opacity-animated (compositor-only) */}
                  <span
                    aria-hidden="true"
                    className="hero-logo-glow absolute inset-0 pointer-events-none"
                    style={{
                      WebkitMaskImage: `url(${HERO_LOGO})`,
                      maskImage: `url(${HERO_LOGO})`,
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                    }}
                  />
                  <img
                    src={HERO_LOGO}
                    alt="DYSSC - Premium Pickleball Paddles Brand Logo"
                    width="3432"
                    height="1008"
                    decoding="async"
                    fetchPriority="high"
                    className="relative h-full w-auto object-contain"
                  />
                </div>
              </div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl leading-none tracking-widest uppercase font-bold text-white -mt-3 ml-[2px] relative">
                {/* Red diagonal swipe that slashes across the headline */}
                <motion.div
                  className="absolute left-0 right-0 -inset-x-12 top-1/2 -translate-y-1/2 h-[3px] pointer-events-none"
                  style={{
                    rotate: -8,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(224,33,40,0) 10%, rgba(224,33,40,0.95) 50%, rgba(224,33,40,0) 90%, transparent 100%)',
                    boxShadow: '0 0 20px rgba(224,33,40,0.7), 0 0 40px rgba(224,33,40,0.4)',
                  }}
                  initial={{ x: '-130%', opacity: 0 }}
                  animate={{ x: '130%', opacity: [0, 1, 1, 0] }}
                  transition={{ duration: 1.4, delay: 0.95, ease: [0.65, 0, 0.35, 1], times: [0, 0.15, 0.85, 1] }}
                />
                <span className="flex flex-wrap gap-x-3 gap-y-2 relative">
                  {['Performance-engineered', 'pickleball'].map((word, i) => (
                    <span key={i} className="inline-block overflow-hidden pb-1">
                      <motion.span
                        className="inline-block"
                        initial={{ y: '110%' }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.85, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {word}
                      </motion.span>
                    </span>
                  ))}
                  <span className="inline-block overflow-hidden pb-1 relative">
                    <motion.span
                      className="inline-block text-white"
                      initial={{ y: '110%' }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.85, delay: 0.74, ease: [0.22, 1, 0.36, 1] }}
                    >
                      paddle.
                    </motion.span>
                    <motion.span
                      className="absolute left-0 right-1 bottom-0 h-[3px] bg-[#E02128] origin-left"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.7, delay: 1.85, ease: 'easeOut' }}
                    />
                  </span>
                </span>
              </h1>
            </motion.div>
            
            <motion.div
              className="flex items-baseline gap-2.5 text-lg md:text-2xl font-heading text-zinc-300"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              data-testid="hero-rotating-tagline"
            >
              <span>Engineered for</span>
              <span className="relative inline-flex overflow-hidden h-[1.35em] min-w-[10ch]">
                <AnimatePresence>
                  <motion.span
                    key={wordIdx}
                    className="font-bold text-white tracking-wider"
                    initial={{ y: '110%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '-110%', opacity: 0, position: 'absolute' }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {POWER_WORDS[wordIdx]}<span className="text-[#E02128]">.</span>
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.div>

            <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-zinc-500 h-5" data-testid="hero-spec-ticker">
              <span ref={tickerRef} />
              <span ref={cursorRef} className="inline-block w-[7px] h-[13px] ml-1.5 align-middle bg-[#E02128]" />
            </p>

            <motion.div
              className="flex flex-wrap items-center gap-x-5 gap-y-4 pt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={handleExplore}
                data-testid="hero-shop-cta"
                className="group inline-flex items-center gap-2.5 px-9 py-4 rounded-full bg-[#E02128] hover:bg-[#B81A20] text-white font-bold uppercase tracking-[0.15em] text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] shadow-[0_12px_30px_rgba(224,33,40,0.28)] hover:shadow-[0_0_24px_rgba(224,33,40,0.45)]"
              >
                Explore
                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
            </motion.div>

            <div className="pt-3">
              <HeroCredentials delay={1.1} />
            </div>
            
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        style={{ zIndex: 10 }}
        data-testid="hero-scroll-indicator"
      >
        <div className="w-6 h-10 rounded-full border-2 border-zinc-600 flex justify-center pt-2">
          <motion.div 
            className="w-1 h-2 bg-zinc-400 rounded-full"
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
      {/* Falling pickleball — bounces on the HexCore wordmark's "C" then exits */}
      {ball !== null && (
        <motion.div
          className="fixed top-0 left-0 z-[90] pointer-events-none w-[68px] h-[68px]"
          data-testid="hero-ball"
          style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
          initial={{ y: ball.top[0], x: ball.left[0], rotate: 0, scaleY: 1 }}
          animate={{ y: ball.top, x: ball.left, scaleY: ball.scaleY, rotate: ball.rotate }}
          transition={{ duration: ball.dur, times: ball.times, ease: 'linear' }}
        >
          <Pickleball className="w-full h-full" />
        </motion.div>
      )}
    </section>
  );
};
